import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Phone, ChevronLeft, ChevronRight, X, ZoomIn, Volume2, VolumeX, Play, ArrowRight } from 'lucide-react';
import { tecnalData } from '../../data/tecnal';
import './ReferenciaPage.css';

const isVideoItem = (item) => typeof item === 'object' && item !== null && item.type === 'video';
const getSrc = (item) => (isVideoItem(item) ? item.src : item);

const ReferenciaPage = () => {
  const { referencia } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [unmutedVideos, setUnmutedVideos] = useState(() => new Set());
  const videoRefs = useRef({});

  const dataKeyMap = {
    'equipos-rotativos': 'referencias-equipos-rotativos',
    'expanders': 'referencias-expanders',
    'enfriadoras': 'referencias-enfriadoras',
    'secadora-vertical': 'referencias-secadora-vertical',
    'plantas-llave-en-mano': 'referencias-plantas-llave-en-mano',
    'otros': 'referencias-otros'
  };

  const dataKey = dataKeyMap[referencia];
  const data = tecnalData[dataKey];

  const images = data ? (data.images || []) : [];
  const basePath = '/assets/tecnal/referencias';

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  const toggleMute = (index) => {
    setUnmutedVideos((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Apply muted state to live <video> elements when set changes
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([idx, el]) => {
      if (!el) return;
      el.muted = !unmutedVideos.has(Number(idx));
    });
  }, [unmutedVideos]);

  // Autoplay/pause videos based on viewport visibility
  useEffect(() => {
    const els = Object.values(videoRefs.current).filter(Boolean);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [images.length]);

  // Keyboard navigation + body scroll lock for lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight') setSelectedImage((prev) => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isLightboxOpen, images.length]);

  if (!data) {
    return (
      <div className="not-found-page" style={{ padding: '4rem', textAlign: 'center' }}>
        <h1>Referencia no encontrada</h1>
        <p>La referencia que busca no está disponible.</p>
      </div>
    );
  }

  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hola, me interesa información sobre ${data.title}`;

  const hasImages = images.length > 0;
  const currentItem = images[selectedImage];

  return (
    <div className="referencia-page" data-testid="referencia-page">
      {/* Hero Section */}
      <section className="referencia-hero">
        <div className="referencia-hero-content">
          <span className="referencia-parent-title">{data.parentTitle}</span>
          <h1 className="referencia-hero-title" data-testid="referencia-title">{data.title}</h1>
          {data.intro && data.intro.map((text, idx) => (
            <p key={idx} className="referencia-hero-description">{text}</p>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      {hasImages ? (
        <section className="referencia-gallery-section">
          <div className="container">
            <h2 className="section-title">Galería de instalaciones</h2>

            <div className="masonry-grid" data-testid="referencia-thumbnails">
              {images.map((item, index) => {
                const src = `${basePath}/${getSrc(item)}`;
                if (isVideoItem(item)) {
                  const isUnmuted = unmutedVideos.has(index);
                  return (
                    <div
                      key={index}
                      className="masonry-item masonry-item--video"
                      onClick={() => openLightbox(index)}
                    >
                      <video
                        ref={(el) => { videoRefs.current[index] = el; }}
                        src={src}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="masonry-video"
                      />
                      <button
                        type="button"
                        className="masonry-mute-toggle"
                        onClick={(e) => { e.stopPropagation(); toggleMute(index); }}
                        aria-label={isUnmuted ? 'Silenciar' : 'Activar sonido'}
                      >
                        {isUnmuted ? <Volume2 size={16} /> : <VolumeX size={16} />}
                      </button>
                      <div className="masonry-overlay">
                        <Play size={26} strokeWidth={1.5} />
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={index}
                    className="masonry-item"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={src}
                      alt={`${data.title} - Imagen ${index + 1}`}
                      loading="lazy"
                      className="masonry-image"
                      onError={(e) => {
                        e.target.src = `https://via.placeholder.com/400x300?text=${encodeURIComponent(data.title)}`;
                      }}
                      data-testid={index === 0 ? 'referencia-main-image' : undefined}
                    />
                    <div className="masonry-overlay">
                      <ZoomIn size={26} strokeWidth={1.5} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : (
        <section className="referencia-gallery-section">
          <div className="container">
            <div className="no-images-placeholder">
              <h3>Galería de instalaciones</h3>
              <p>Imágenes de nuestras instalaciones próximamente disponibles.</p>
              <p>Contactenos para obtener más información sobre nuestras referencias.</p>
            </div>
          </div>
        </section>
      )}

      {/* Ver ficha técnica del equipo */}
      {data.equipmentHref && (
        <section className="referencia-equipo-section">
          <div className="container">
            <p className="referencia-equipo-text">¿Querés conocer más sobre este equipo?</p>
            <Button
              size="lg"
              className="referencia-equipo-button"
              onClick={() => navigate(data.equipmentHref)}
            >
              Ver ficha técnica
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="referencia-cta-section">
        <div className="container">
          <h2 className="referencia-cta-title">
            ¿Desea conocer más sobre nuestras instalaciones?
          </h2>
          <p className="referencia-cta-subtitle">
            Contactanos para visitar nuestras referencias o solicitar información técnica
          </p>
          <Button
            size="lg"
            className="referencia-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
            data-testid="referencia-contact-button"
          >
            <Phone className="mr-2" size={20} />
            Contactar
          </Button>
        </div>
      </section>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)} data-testid="lightbox">
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>

            <div className="lightbox-image-area">
              {images.length > 1 && (
                <button
                  className="lightbox-nav prev"
                  onClick={() => setSelectedImage((prev) => (prev - 1 + images.length) % images.length)}
                  aria-label="Anterior"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              {isVideoItem(currentItem) ? (
                <video
                  key={`lb-${selectedImage}`}
                  src={`${basePath}/${getSrc(currentItem)}`}
                  controls
                  autoPlay
                  playsInline
                  className="lightbox-image lightbox-video"
                />
              ) : (
                <img
                  key={`lb-${selectedImage}`}
                  src={`${basePath}/${getSrc(currentItem)}`}
                  alt={`${data.title} - Imagen ${selectedImage + 1}`}
                  className="lightbox-image"
                />
              )}
              {images.length > 1 && (
                <button
                  className="lightbox-nav next"
                  onClick={() => setSelectedImage((prev) => (prev + 1) % images.length)}
                  aria-label="Siguiente"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>

            <div className="lightbox-footer">
              <span className="lightbox-counter">{selectedImage + 1} / {images.length}</span>
              {images.length > 1 && (
                <div className="lightbox-thumbs">
                  {images.map((item, index) => {
                    const isVid = isVideoItem(item);
                    return (
                      <button
                        key={index}
                        className={`lightbox-thumb${index === selectedImage ? ' active' : ''}${isVid ? ' lightbox-thumb--video' : ''}`}
                        onClick={() => setSelectedImage(index)}
                        aria-label={`Ver ${isVid ? 'video' : 'imagen'} ${index + 1}`}
                      >
                        {isVid ? (
                          <>
                            <video
                              src={`${basePath}/${getSrc(item)}`}
                              muted
                              preload="metadata"
                              className="lightbox-thumb-img"
                            />
                            <span className="lightbox-thumb-play"><Play size={14} /></span>
                          </>
                        ) : (
                          <img
                            src={`${basePath}/${getSrc(item)}`}
                            alt=""
                            className="lightbox-thumb-img"
                            onError={(e) => { e.target.src = `https://via.placeholder.com/80x60?text=${index + 1}`; }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenciaPage;
